<script setup>
/**
 * Expediente del agente a PANTALLA COMPLETA (Victor 2026-06-06).
 * Izquierda: KPIs · Centro: clientes que respondió (con buscador) ·
 * Derecha: ESPEJO EN VIVO del chat seleccionado (Propuesta 3) que se
 * autorefresca cada 5s para ver lo que el agente responde en tiempo real.
 * Solo lectura. Nota: WhatsApp no expone el borrador que el agente teclea;
 * el espejo muestra los mensajes ya enviados, en vivo.
 */
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AdvancedMetricsAPI from 'dashboard/api/advancedMetrics';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  show: { type: Boolean, default: false },
  agentId: { type: [Number, String], default: null },
  agentName: { type: String, default: '' },
  branchId: { type: [Number, String], default: null },
  // Métrica clickeada (Victor 2026-06-07): si viene, el modal muestra SOLO los
  // clientes de esa métrica (de hoy). Si es null → expediente clásico.
  metric: { type: String, default: null },
  metricLabel: { type: String, default: '' },
});
const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();

const REFRESH_MS = 5000;

const loading = ref(false);
const data = ref(null);
const day = ref('hoy');
const search = ref('');
const mobileTab = ref('clientes');

const selected = ref(null); // { id, contact, inbox }
const mirror = ref({ conversation: null, messages: [] });
const threadEl = ref(null);
let pollTimer = null;

// Compositor del espejo: responder al cliente o dejar nota interna.
const composeMode = ref('reply'); // 'reply' | 'note'
const composeText = ref('');
const sending = ref(false);
const sendError = ref(false);

const DAYS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: '7d', label: '7 días' },
];

const rangeFor = id => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (id === 'ayer') {
    const s = new Date(start);
    s.setDate(s.getDate() - 1);
    const u = new Date(start);
    u.setSeconds(-1);
    return { since: s.toISOString(), until: u.toISOString() };
  }
  if (id === '7d') {
    const s = new Date(start);
    s.setDate(s.getDate() - 6);
    return { since: s.toISOString(), until: now.toISOString() };
  }
  return { since: start.toISOString(), until: now.toISOString() };
};

const fetchDetail = async () => {
  if (!props.agentId) return;
  loading.value = true;
  try {
    const { since, until } = rangeFor(day.value);
    const params = { agent_id: props.agentId, since, until };
    if (props.branchId) params.branch_id = props.branchId;
    if (props.metric) params.metric = props.metric;
    const res = await AdvancedMetricsAPI.agentDetail(params);
    data.value = res.data;
    // Espejo por defecto: en modo métrica, el primer cliente de esa métrica; en
    // el expediente clásico, a quién atiende ahora (o el primero).
    const def = props.metric
      ? data.value?.conversations?.[0]
      : data.value?.attending_now || data.value?.conversations?.[0];
    if (def) selectConv(def);
    else {
      selected.value = null;
      mirror.value = { conversation: null, messages: [] };
    }
  } catch (e) {
    data.value = null;
  } finally {
    loading.value = false;
  }
};

const fetchMirror = async () => {
  if (!selected.value?.id) return;
  try {
    const res = await AdvancedMetricsAPI.conversationMessages({
      conversation_id: selected.value.id,
    });
    mirror.value = res.data || { conversation: null, messages: [] };
    await nextTick();
    if (threadEl.value) threadEl.value.scrollTop = threadEl.value.scrollHeight;
  } catch (e) {
    /* silencioso: el espejo reintenta en el próximo refresco */
  }
};

const startPoll = () => {
  stopPoll();
  pollTimer = setInterval(fetchMirror, REFRESH_MS);
};
const stopPoll = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};

const selectConv = conv => {
  selected.value = conv;
  mobileTab.value = 'envivo';
  composeText.value = '';
  sendError.value = false;
  fetchMirror();
  startPoll();
};

// Envía la respuesta al cliente (WhatsApp) o la nota interna (privada).
const sendCompose = async () => {
  const content = composeText.value.trim();
  if (!content || sending.value || !selected.value?.id) return;
  sending.value = true;
  sendError.value = false;
  try {
    await ConversationApi.sendMessage(selected.value.id, {
      content,
      isPrivate: composeMode.value === 'note',
    });
    composeText.value = '';
    await fetchMirror();
  } catch (e) {
    sendError.value = true;
  } finally {
    sending.value = false;
  }
};

watch(
  () => [props.show, props.agentId, props.metric],
  () => {
    if (props.show) {
      day.value = 'hoy';
      search.value = '';
      mobileTab.value = 'clientes';
      fetchDetail();
    } else {
      stopPoll();
    }
  }
);

onUnmounted(stopPoll);

const setDay = id => {
  day.value = id;
  fetchDetail();
};

const profile = computed(() => data.value?.profile || {});
const kpis = computed(() => data.value?.kpis || {});
const conversations = computed(() => {
  const list = data.value?.conversations || [];
  const q = search.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter(c => (c.contact || '').toLowerCase().includes(q));
});

// Estado del agente: con cliente (atendiendo ahora) o sin cliente.
const attendingNow = computed(() => data.value?.attending_now || null);
const lastActivityAt = computed(
  () => attendingNow.value?.last_at || conversations.value[0]?.last_at || null
);

// "1 min" / "2 h" / "un momento" — compacto para la barra de estado.
const compactSince = iso => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'un momento';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return `${Math.floor(diff / 86400)} d`;
};

// Franja del día a la que pertenece un mensaje (más reciente arriba).
const bandFor = iso => {
  const d = new Date(iso);
  const diffMin = (Date.now() - d.getTime()) / 60000;
  if (diffMin <= 20) return 'Hace un momento';
  const h = d.getHours();
  if (h >= 19) return 'Noche';
  if (h >= 12) return 'Tarde';
  return 'Mañana';
};

// Lista ya ordenada por last_at desc → agrupamos en bandas conservando el orden.
const groupedConversations = computed(() => {
  const groups = [];
  let current = null;
  conversations.value.forEach(c => {
    const band = bandFor(c.last_at);
    if (!current || current.band !== band) {
      current = { band, items: [] };
      groups.push(current);
    }
    current.items.push(c);
  });
  return groups;
});

// Espejo en orden de WhatsApp: el primer mensaje arriba, el más reciente abajo.
// Se ordena ascendente por fecha aunque el backend ya lo entregue así (idempotente).
const orderedMessages = computed(() => {
  const list = mirror.value?.messages || [];
  return [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
});

const initials = name =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const fmtSecs = s => {
  if (!s || s <= 0) return '0s';
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};
const fmtTime = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const close = () => {
  stopPoll();
  emit('close');
};
const openConversation = () => {
  if (!selected.value?.id) return;
  const accountId = route.params.accountId;
  close();
  router.push(`/app/accounts/${accountId}/conversations/${selected.value.id}`);
};
</script>

<template>
  <teleport to="body">
    <div v-if="show" class="ag-fs">
      <!-- HEADER -->
      <div class="ag-head">
        <div class="ag-av">{{ initials(profile.name || agentName) }}</div>
        <div class="ag-id">
          <div class="ag-name">{{ profile.name || agentName }}</div>
          <div class="ag-sub">{{ profile.branch || '—' }} · {{ profile.role }}</div>
        </div>
        <span v-if="metric" class="ag-metricchip">{{ metricLabel }} · {{ data?.conversations_total ?? 0 }}</span>
        <span class="ag-pill" :class="profile.online ? 'on' : 'off'">
          <span class="ag-dot" :class="{ live: profile.online }" />{{ profile.online ? 'En línea' : 'Sin actividad' }}
        </span>
        <div class="ag-hkpis">
          <div class="ag-hk"><div class="v">{{ kpis.attended ?? 0 }}</div><div class="l">Atend.</div></div>
          <div class="ag-hk"><div class="v">{{ kpis.responses ?? 0 }}</div><div class="l">Resp.</div></div>
          <div class="ag-hk"><div class="v">{{ fmtSecs(kpis.avg_response) }}</div><div class="l">1ª resp</div></div>
          <div class="ag-hk"><div class="v" :class="{ red: kpis.urgent > 0 }">{{ kpis.urgent ?? 0 }}</div><div class="l">Urgent.</div></div>
        </div>
        <div v-if="!metric" class="ag-days">
          <button
            v-for="d in DAYS"
            :key="d.id"
            class="ag-day"
            :class="{ on: day === d.id }"
            @click="setDay(d.id)"
          >
            {{ d.label }}
          </button>
        </div>
        <button class="ag-x" @click="close">✕</button>
      </div>

      <!-- TABS (solo celular) -->
      <div class="ag-tabs">
        <button class="ag-tab" :class="{ on: mobileTab === 'resumen' }" @click="mobileTab = 'resumen'">Resumen</button>
        <button class="ag-tab" :class="{ on: mobileTab === 'clientes' }" @click="mobileTab = 'clientes'">Clientes</button>
        <button class="ag-tab" :class="{ on: mobileTab === 'envivo' }" @click="mobileTab = 'envivo'">En vivo</button>
      </div>

      <!-- BODY -->
      <div class="ag-body" :data-tab="mobileTab">
        <!-- KPIs -->
        <aside class="ag-c1 zone" data-zone="resumen">
          <div class="ag-csec">Resumen · {{ DAYS.find(d => d.id === day).label }}</div>
          <div class="ag-kpis">
            <div class="ag-k"><div class="v">{{ kpis.attended ?? 0 }}</div><div class="l">Atendidos</div></div>
            <div class="ag-k"><div class="v">{{ kpis.closed ?? 0 }}</div><div class="l">Cerrados</div></div>
            <div class="ag-k"><div class="v">{{ kpis.responses ?? 0 }}</div><div class="l">Respuestas</div></div>
            <div class="ag-k"><div class="v">{{ fmtSecs(kpis.avg_response) }}</div><div class="l">1ª resp.</div></div>
            <div class="ag-k"><div class="v" :class="{ red: kpis.urgent > 0 }">{{ kpis.urgent ?? 0 }}</div><div class="l">En urgentes</div></div>
            <div class="ag-k"><div class="v">{{ kpis.converted ?? 0 }}</div><div class="l">Convertidos</div></div>
          </div>
        </aside>

        <!-- CLIENTES -->
        <section class="ag-c2 zone" data-zone="clientes">
          <div class="ag-c2head">
            <span class="ag-c2title">{{ metric ? 'Clientes · ' + metricLabel : 'Clientes que respondió' }}</span>
            <span v-if="data" class="ag-count">{{ data.conversations_total ?? conversations.length }}</span>
          </div>
          <!-- Estado del agente: con cliente / sin cliente -->
          <div class="ag-status" :class="attendingNow ? 'busy' : 'idle'">
            <span class="ag-status-ic"><span class="ag-dot" :class="{ live: attendingNow }" /></span>
            <div class="ag-status-txt">
              <div class="l">{{ attendingNow ? 'Atendiendo ahora' : 'Sin cliente ahora' }}</div>
              <div class="v" :class="{ muted: !attendingNow }">
                {{ attendingNow ? attendingNow.contact : 'Libre' }}
              </div>
            </div>
            <div class="ag-status-since">
              <span>{{ attendingNow ? 'activo hace' : 'libre hace' }}</span>
              <b>{{ compactSince(lastActivityAt) }}</b>
            </div>
          </div>
          <div class="ag-search">
            <span>🔎</span>
            <input v-model="search" type="text" placeholder="Buscar cliente…" />
          </div>
          <div v-if="loading" class="ag-state">Cargando…</div>
          <div v-else-if="!conversations.length" class="ag-state">Sin clientes en este período.</div>
          <div v-else class="ag-clist">
            <template v-for="g in groupedConversations" :key="g.band">
              <div class="ag-cband">{{ g.band }}</div>
              <div
                v-for="c in g.items"
                :key="c.id"
                class="ag-cl"
                :class="{ now: attendingNow && attendingNow.id === c.id, active: selected && selected.id === c.id }"
                @click="selectConv(c)"
              >
                <span class="ag-av sm">{{ initials(c.contact) }}</span>
                <div class="ag-cl-info">
                  <div class="ag-cl-nm">
                    <span class="ag-cl-name">{{ c.contact }}</span>
                    <span v-if="attendingNow && attendingNow.id === c.id" class="ag-nowtag">En vivo</span>
                    <span v-else-if="c.channel" class="ag-chan">{{ c.channel }}</span>
                  </div>
                  <div class="ag-cl-ms" :class="{ empty: !c.last_message }">{{ c.last_message || 'Sin texto' }}</div>
                </div>
                <span class="ag-cl-tm">{{ fmtTime(c.last_at) }}</span>
              </div>
            </template>
          </div>
        </section>

        <!-- ESPEJO EN VIVO -->
        <section class="ag-c3 zone" data-zone="envivo">
          <template v-if="selected">
            <div class="ag-mhead">
              <span class="ag-av sm">{{ initials(mirror.conversation?.contact || selected.contact) }}</span>
              <div class="ag-mhead-id">
                <div class="ag-mname">{{ mirror.conversation?.contact || selected.contact }}</div>
                <div class="ag-msub">{{ mirror.conversation?.inbox || selected.inbox }}</div>
              </div>
              <span class="ag-livelab"><span class="ag-dot live" /> Espejo en vivo</span>
              <button class="ag-openlink" @click="openConversation">Abrir chat →</button>
            </div>
            <div ref="threadEl" class="ag-thread">
              <div class="ag-thread-inner">
                <div v-if="!orderedMessages.length" class="ag-state">Cargando conversación…</div>
                <template v-else>
                  <div class="ag-daysep">Hoy</div>
                  <div
                    v-for="m in orderedMessages"
                    :key="m.id"
                    class="ag-b"
                    :class="m.private ? 'note' : (m.outgoing ? 'out' : 'in')"
                  >
                    <div v-if="m.private" class="ag-b-note">★ Nota interna</div>
                    <div class="ag-b-c">{{ m.content }}</div>
                    <div class="ag-b-t">
                      {{ fmtTime(m.created_at) }}
                      <span v-if="m.outgoing && !m.private" class="ag-tick" :class="{ read: m.status === 'read' }">
                        {{ m.status === 'sent' ? '✓' : '✓✓' }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <!-- Compositor: responder al cliente o nota interna -->
            <div class="ag-compose">
              <div class="ag-ctoggle">
                <button class="ag-ctab" :class="{ 'on-reply': composeMode === 'reply' }" @click="composeMode = 'reply'">💬 Responder</button>
                <button class="ag-ctab" :class="{ 'on-note': composeMode === 'note' }" @click="composeMode = 'note'">★ Nota interna</button>
              </div>
              <div class="ag-cbox" :class="{ note: composeMode === 'note' }">
                <textarea
                  v-model="composeText"
                  class="ag-cinput"
                  rows="1"
                  :placeholder="composeMode === 'note' ? 'Nota interna — solo la ve el equipo…' : `Escribe un mensaje a ${selected.contact}…`"
                  @keydown.enter.exact.prevent="sendCompose"
                />
                <button
                  class="ag-csend"
                  :class="composeMode === 'note' ? 'note' : 'reply'"
                  :disabled="sending || !composeText.trim()"
                  @click="sendCompose"
                >➤</button>
              </div>
              <div class="ag-chint" :class="{ err: sendError }">
                {{ sendError
                  ? 'No se pudo enviar, intenta de nuevo.'
                  : (composeMode === 'note'
                    ? 'No le llega al cliente · queda en el hilo en ámbar.'
                    : 'Enter envía · sale por WhatsApp a nombre del agente.') }}
              </div>
            </div>
          </template>
          <div v-else class="ag-state ag-state-big">Selecciona un cliente para ver el chat en vivo</div>
        </section>
      </div>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.ag-fs {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: radial-gradient(900px 400px at 75% -10%, #161618, #08080a 60%);
}
.ag-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.ag-av {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2a2a2e, #161618);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  place-items: center;
  font-weight: 700;
  color: #d8bd86;
  flex: none;
}
.ag-av.sm { width: 30px; height: 30px; font-size: 11px; }
.ag-name { font-size: 15.5px; font-weight: 700; color: #fff; }
.ag-sub { font-size: 11px; color: #8b8b91; margin-top: 1px; }
.ag-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
}
.ag-metricchip { display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 800; color: #d8bd86; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.5); border-radius: 999px; padding: 5px 13px; }
.ag-pill.on { color: #5fae7e; background: rgba(95, 174, 126, 0.14); border: 1px solid rgba(95, 174, 126, 0.35); }
.ag-pill.off { color: #8b8b91; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); }
.ag-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.ag-dot.live { background: #5fae7e; box-shadow: 0 0 0 3px rgba(95, 174, 126, 0.2); animation: agpulse 1.4s infinite; }
@keyframes agpulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
.ag-days { margin-left: auto; display: flex; gap: 6px; }
.ag-day { font-size: 11px; font-weight: 600; padding: 6px 12px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); color: #8b8b91; background: none; cursor: pointer; }
.ag-day.on { color: #d8bd86; background: rgba(201, 163, 90, 0.12); border-color: rgba(201, 163, 90, 0.5); }
.ag-x { width: 34px; height: 34px; border-radius: 9px; border: 1px solid rgba(255, 255, 255, 0.08); display: grid; place-items: center; color: #8b8b91; cursor: pointer; background: none; font-size: 15px; }
.ag-x:hover { color: #fff; }

.ag-tabs { display: none; }

/* Chips de KPIs en el header (escritorio) */
.ag-hkpis { display: flex; gap: 7px; margin-left: 6px; }
.ag-hk { background: #121214; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 9px; padding: 5px 11px; text-align: center; min-width: 52px; }
.ag-hk .v { font-size: 14px; font-weight: 800; color: #fff; line-height: 1.1; } .ag-hk .v.red { color: #cc6b6b; }
.ag-hk .l { font-size: 7.5px; text-transform: uppercase; letter-spacing: 0.4px; color: #6c6c72; margin-top: 1px; }

.ag-body {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 0;
}
.zone { min-height: 0; display: flex; flex-direction: column; }
/* Panel de KPIs: oculto en escritorio (van en el header), visible en celular bajo "Resumen". */
.ag-c1 { display: none; border-right: 1px solid rgba(255, 255, 255, 0.07); padding: 18px; background: linear-gradient(180deg, #121214, #0d0d0f); overflow-y: auto; }
.ag-csec { font-size: 9.5px; letter-spacing: 1.4px; text-transform: uppercase; color: #6c6c72; margin-bottom: 10px; }
.ag-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.ag-k { background: #141416; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 10px; padding: 11px; text-align: center; }
.ag-k .v { font-size: 18px; font-weight: 800; } .ag-k .v.red { color: #cc6b6b; }
.ag-k .l { font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.4px; color: #6c6c72; margin-top: 3px; }

.ag-c2 { border-right: 1px solid rgba(255, 255, 255, 0.07); }
.ag-c2head { display: flex; align-items: center; gap: 10px; padding: 16px 18px 10px; }
.ag-c2title { font-size: 13.5px; font-weight: 700; color: #fff; }
.ag-count { font-size: 10px; color: #d8bd86; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.5); border-radius: 999px; padding: 2px 9px; font-weight: 700; }
.ag-search { margin: 0 18px 10px; display: flex; align-items: center; gap: 8px; background: #121214; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 9px; padding: 8px 11px; }
.ag-search input { background: none; border: none; outline: none; color: #e9e9ec; font-size: 12.5px; width: 100%; }
.ag-cl-info { flex: 1; min-width: 0; }
.ag-cl-nm { font-size: 12.5px; font-weight: 600; color: #fff; display: flex; align-items: center; gap: 7px; min-width: 0; }
.ag-chan { font-size: 8px; text-transform: uppercase; letter-spacing: 0.5px; color: #6c6c72; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 5px; padding: 1px 5px; }
.ag-cl-ms { font-size: 11.5px; color: #8b8b91; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ag-cl-tm { font-size: 10px; color: #6c6c72; flex: none; align-self: flex-start; }

/* Barra de estado del agente */
.ag-status { margin: 0 18px 12px; border-radius: 12px; padding: 11px 13px; display: flex; align-items: center; gap: 11px; }
.ag-status.busy { background: linear-gradient(180deg, rgba(95, 174, 126, 0.16), rgba(95, 174, 126, 0.05)); border: 1px solid rgba(95, 174, 126, 0.4); }
.ag-status.idle { background: #121214; border: 1px solid rgba(255, 255, 255, 0.07); }
.ag-status-ic { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; flex: none; }
.ag-status.busy .ag-status-ic { background: rgba(95, 174, 126, 0.14); border: 1px solid rgba(95, 174, 126, 0.4); }
.ag-status.idle .ag-status-ic { background: #1a1a1d; border: 1px solid rgba(255, 255, 255, 0.08); }
.ag-status-ic .ag-dot { width: 10px; height: 10px; }
.ag-status.idle .ag-status-ic .ag-dot { background: #6c6c72; }
.ag-status-txt .l { font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase; font-weight: 800; }
.ag-status.busy .ag-status-txt .l { color: #5fae7e; }
.ag-status.idle .ag-status-txt .l { color: #6c6c72; }
.ag-status-txt .v { font-size: 13.5px; font-weight: 700; color: #fff; margin-top: 2px; }
.ag-status-txt .v.muted { color: #8b8b91; }
.ag-status-since { margin-left: auto; text-align: right; font-size: 10px; color: #6c6c72; }
.ag-status-since b { display: block; color: #e9e9ec; font-size: 13px; font-weight: 700; }

/* Lista de clientes (filas limpias agrupadas por franja) */
.ag-clist { overflow-y: auto; padding: 2px 14px 16px; }
.ag-cband { font-size: 8.5px; letter-spacing: 1.3px; text-transform: uppercase; color: #6c6c72; font-weight: 700; margin: 11px 6px 4px; }
.ag-cl { display: flex; align-items: center; gap: 11px; padding: 9px 10px; border-radius: 10px; cursor: pointer; border: 1px solid transparent; transition: 0.12s; }
.ag-cl:hover { background: #101012; }
.ag-cl.active { background: rgba(201, 163, 90, 0.1); border-color: rgba(201, 163, 90, 0.45); }
.ag-cl.now { background: rgba(95, 174, 126, 0.12); border-color: rgba(95, 174, 126, 0.4); }
.ag-cl-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ag-cl-ms.empty { color: #6c6c72; font-style: italic; }
.ag-nowtag { font-size: 7.5px; font-weight: 800; letter-spacing: 0.5px; color: #5fae7e; text-transform: uppercase; border: 1px solid rgba(95, 174, 126, 0.4); border-radius: 5px; padding: 1px 5px; flex: none; }

.ag-c3 { background: radial-gradient(500px 300px at 50% -20%, #121316, #0b0b0d); }
.ag-mhead { display: flex; align-items: center; gap: 9px; padding: 14px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.07); }
.ag-mhead-id { min-width: 0; }
.ag-mname { font-size: 14px; font-weight: 700; color: #fff; }
.ag-msub { font-size: 10.5px; color: #8b8b91; }
.ag-livelab { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase; color: #5fae7e; font-weight: 800; }
.ag-openlink { font-size: 11px; font-weight: 700; color: #d8bd86; background: rgba(201, 163, 90, 0.1); border: 1px solid rgba(201, 163, 90, 0.4); border-radius: 8px; padding: 5px 10px; cursor: pointer; flex: none; }
.ag-openlink:hover { background: rgba(201, 163, 90, 0.18); }
.ag-thread { flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
.ag-thread-inner { margin-top: auto; display: flex; flex-direction: column; gap: 9px; padding: 16px 20px; }
.ag-daysep { align-self: center; font-size: 9px; color: #6c6c72; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 999px; padding: 2px 11px; margin-bottom: 4px; }
.ag-b { max-width: 66%; border-radius: 13px; padding: 9px 12px; font-size: 13px; line-height: 1.45; }
.ag-b.in { align-self: flex-start; background: #1b1b1e; border: 1px solid rgba(255, 255, 255, 0.07); border-top-left-radius: 4px; }
.ag-b.out { align-self: flex-end; background: linear-gradient(180deg, #12463463, #0d3528); border: 1px solid rgba(255, 255, 255, 0.05); border-top-right-radius: 4px; }
.ag-b.note { align-self: flex-end; background: rgba(231, 192, 107, 0.12); border: 1px solid rgba(231, 192, 107, 0.45); border-top-right-radius: 4px; color: #f0dcae; }
.ag-b-note { font-size: 7.5px; font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; color: #e7c06b; margin-bottom: 3px; }
.ag-b-t { font-size: 8.5px; color: #6c6c72; margin-top: 3px; text-align: right; }
.ag-tick { color: #6c6c72; } .ag-tick.read { color: #7fb4ff; }

/* Compositor (responder / nota interna) */
.ag-compose { border-top: 1px solid rgba(255, 255, 255, 0.07); padding: 10px 14px 12px; background: #0c0c0e; }
.ag-ctoggle { display: flex; gap: 6px; margin-bottom: 9px; }
.ag-ctab { font-size: 11px; font-weight: 700; padding: 5px 13px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); color: #8b8b91; background: none; cursor: pointer; }
.ag-ctab.on-reply { color: #5fae7e; background: rgba(95, 174, 126, 0.14); border-color: rgba(95, 174, 126, 0.4); }
.ag-ctab.on-note { color: #e7c06b; background: rgba(231, 192, 107, 0.12); border-color: rgba(231, 192, 107, 0.45); }
.ag-cbox { display: flex; align-items: flex-end; gap: 10px; background: #141416; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 12px; padding: 8px 10px; }
.ag-cbox.note { background: rgba(231, 192, 107, 0.1); border-color: rgba(231, 192, 107, 0.45); }
.ag-cinput { flex: 1; background: none; border: none; outline: none; resize: none; color: #e9e9ec; font-size: 13px; line-height: 1.4; max-height: 110px; font-family: inherit; padding: 4px 2px; }
.ag-cinput::placeholder { color: #6c6c72; }
.ag-csend { width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; flex: none; font-size: 15px; color: #1a1206; font-weight: 800; border: none; cursor: pointer; }
.ag-csend.reply { background: #c9a35a; }
.ag-csend.note { background: #e7c06b; }
.ag-csend:disabled { opacity: 0.4; cursor: default; }
.ag-chint { font-size: 9.5px; color: #6c6c72; margin-top: 7px; padding-left: 2px; }
.ag-chint.err { color: #cc6b6b; }
.ag-state { padding: 26px 18px; text-align: center; color: #8b8b91; font-size: 13px; }
.ag-state-big { margin: auto; }

@media (max-width: 860px) {
  .ag-tabs { display: flex; gap: 6px; padding: 10px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.07); }
  .ag-tab { flex: 1; font-size: 12px; font-weight: 600; padding: 8px; border-radius: 9px; border: 1px solid rgba(255, 255, 255, 0.08); color: #8b8b91; background: none; }
  .ag-tab.on { color: #d8bd86; background: rgba(201, 163, 90, 0.12); border-color: rgba(201, 163, 90, 0.5); }
  .ag-days, .ag-hkpis { display: none; }
  .ag-body { display: block; }
  .zone { display: none; height: 100%; border-right: none; }
  /* En celular el panel de KPIs sí se muestra bajo la pestaña Resumen. */
  .ag-body[data-tab='resumen'] .zone[data-zone='resumen'] { display: flex; }
  .ag-body[data-tab='clientes'] .zone[data-zone='clientes'] { display: flex; }
  .ag-body[data-tab='envivo'] .zone[data-zone='envivo'] { display: flex; }
  .ag-b { max-width: 80%; }
}
</style>
